


import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  MagnifyingGlassIcon,
  MinusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import TableComponent from "../../components/TableComponent";
import CommentSearchModal from "../../components/admin/CommentSearchModal";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/helps";
import {
  fetchFeedback,
  updateFeedback,
} from "../../features/admin/FeedbackSlice";
import AuditHistoryButton, {
  canViewAuditHistory,
} from "../../components/admin/AuditHistoryButton";

const EMPTY_FILTERS = {
  search: "",
  postId: "",
  status: "",
  postSearch: "",
  commentSearch: "",
  page: 1,
  limit: 1000,
};

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((permission) => permission.permissionSlug)
    : [];
};

const hasPermission = (requiredPermission) => {
  const userId = localStorage.getItem("userId");

  if (userId == 1) {
    return true;
  }

  return getUserPermissions().includes(requiredPermission);
};

const Feedback = () => {
  const dispatch = useDispatch();
  const { posts } = useSelector((state) => state.posts);
  const {
    feedbacks,
    totalItems,
    status,
    error,
  } = useSelector((state) => state.feedbacks);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [commonSearch, setCommonSearch] = useState("");

  useEffect(() => {
    dispatch(fetchFeedback(filters));
  }, [dispatch, filters]);

  const activeFilterCount = useMemo(() => {
    return [
      filters.search,
      filters.postId,
      filters.status,
      filters.postSearch,
      filters.commentSearch,
    ].filter(Boolean).length;
  }, [filters]);

  const hasAppliedFilter = activeFilterCount > 0;

  const handleUpdateStatus = async (id, nextStatus) => {
    try {
      await dispatch(
        updateFeedback({
          id,
          data: {
            status: nextStatus,
          },
        })
      ).unwrap();

      toast.success(
        nextStatus === "approved"
          ? "Duyệt bình luận thành công!"
          : "Từ chối bình luận thành công!"
      );
      dispatch(fetchFeedback(filters));
    } catch (updateError) {
      toast.error(
        updateError?.messages || "Đã xảy ra lỗi khi cập nhật bình luận!"
      );
    }
  };

  const handleCommonSearch = () => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      search: commonSearch.trim(),
      page: 1,
      limit: 1000,
    }));
  };

  const handleCommonSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleCommonSearch();
    }
  };

  const handleClearCommonSearch = () => {
    setCommonSearch("");
    setFilters((currentFilters) => ({
      ...currentFilters,
      search: "",
      page: 1,
      limit: 1000,
    }));
  };

  const handleApplyFilters = (nextFilters) => {
    setFilters((currentFilters) => ({
      ...EMPTY_FILTERS,
      search: currentFilters.search,
      ...nextFilters,
      page: 1,
      limit: 1000,
    }));
    setIsSearchModalOpen(false);
  };

  const handleResetFilters = () => {
    setCommonSearch("");
    setFilters(EMPTY_FILTERS);
  };

  const getPostTitle = (item) => {
    if (item?.post?.title) {
      return item.post.title;
    }

    return (
      posts?.find((post) => post.id === item.postId)?.title || "Không xác định"
    );
  };

  const headers = [
    {
      key: "post",
      label: "Tên bài viết",
      render: (item) => (
        <div className="min-w-64 max-w-xl whitespace-normal">
          <p className="font-medium">{getPostTitle(item)}</p>
        </div>
      ),
    },
    {
      key: "content",
      label: "Bình luận",
      render: (item) => (
        <div className="min-w-64 max-w-xl whitespace-normal">
          <p>{item.content}</p>
        </div>
      ),
    },
    {
      key: "user",
      label: "Người phản hồi",
      render: (item) => (
        <div className="min-w-40">
          <p className="font-medium">
            {item.user?.userFullName || "Không xác định"}
          </p>
        </div>
      ),
    },
    {
      key: "status",
      label: "Trạng thái",
      render: (item) => {
        const statusStyles = {
          approved: "bg-green-100 text-green-800",
          rejected: "bg-red-100 text-red-800",
          pending: "bg-yellow-100 text-yellow-800",
        };
        const statusLabels = {
          approved: "Đã duyệt",
          rejected: "Từ chối",
          pending: "Chờ duyệt",
        };

        return (
          <span
            className={`whitespace-nowrap rounded-full px-3 py-1 text-sm ${
              statusStyles[item.status] || "bg-gray-100 text-gray-700"
            }`}
          >
            {statusLabels[item.status] || "Chờ duyệt"}
          </span>
        );
      },
    },
    {
      key: "updatedAt",
      label: "Ngày cập nhật",
      render: (item) => (
        <div className="min-w-32">
          <p className="font-medium">{formatDate(item.updatedAt)}</p>
        </div>
      ),
    },
  ];

  if (hasPermission("COMMENT_APPROVE") || canViewAuditHistory()) {
    headers.push({ key: "setting", label: "Thao tác" });
  }

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {hasPermission("COMMENT_APPROVE") && (
          <button
            type="button"
            onClick={() => handleUpdateStatus(item.id, "approved")}
            title="Duyệt"
            className="btn btn-outline btn-accent btn-sm my-1 rounded px-3"
          >
            <CheckBadgeIcon className="h-5 w-5" />
          </button>
        )}
        {hasPermission("COMMENT_APPROVE") && (
          <button
            type="button"
            onClick={() => handleUpdateStatus(item.id, "rejected")}
            title="Từ chối"
            className="btn btn-outline btn-error btn-sm my-1 rounded px-3"
          >
            <MinusCircleIcon className="h-5 w-5" />
          </button>
        )}
        <AuditHistoryButton
          module="COMMENT"
          entityType="Comment"
          entityId={item.id}
        />
      </div>
    ),
  };

  if (status === "loading" && feedbacks.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="rounded-md bg-red-50 p-4 text-red-500">
        Đã xảy ra lỗi: {error?.messages || error?.message || String(error)}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Quản lý bình luận</h1>
          <p className="mt-1 text-sm text-gray-500">
            {hasAppliedFilter ? "Tìm thấy" : "Có"}{" "}
            {totalItems ?? feedbacks.length} bình luận.
          </p>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row xl:w-auto">
          <div className="flex min-w-0 flex-1 sm:min-w-[360px]">
            <div className="relative min-w-0 flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={commonSearch}
                onChange={(event) => setCommonSearch(event.target.value)}
                onKeyDown={handleCommonSearchKeyDown}
                placeholder="Tìm bài viết, bình luận hoặc người phản hồi..."
                className="input input-bordered w-full rounded-r-none pl-10"
              />
              {commonSearch && (
                <button
                  type="button"
                  onClick={handleClearCommonSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700"
                  title="Xóa tìm kiếm"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={handleCommonSearch}
              className="btn btn-primary rounded-l-none px-4"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
              Tìm
            </button>
          </div>

          <button
            type="button"
            onClick={() => setIsSearchModalOpen(true)}
            className="btn btn-primary whitespace-nowrap"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
            Tìm kiếm nâng cao
            {activeFilterCount > 0 && (
              <span className="badge badge-neutral ml-1">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeFilterCount > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border bg-base-200 p-3 text-sm">
          {filters.search && (
            <span className="badge badge-outline">
              Tìm chung: {filters.search}
            </span>
          )}
          {filters.postSearch && (
            <span className="badge badge-outline">
              Bài viết: {filters.postSearch}
            </span>
          )}
          {filters.commentSearch && (
            <span className="badge badge-outline">
              Bình luận: {filters.commentSearch}
            </span>
          )}
          {filters.postId && (
            <span className="badge badge-outline">
              Đã chọn bài viết cụ thể
            </span>
          )}
          {filters.status && (
            <span className="badge badge-outline">
              Trạng thái:{" "}
              {filters.status === "approved"
                ? "Đã duyệt"
                : filters.status === "rejected"
                ? "Từ chối"
                : "Chờ duyệt"}
            </span>
          )}

          <button
            type="button"
            onClick={handleResetFilters}
            className="btn btn-ghost btn-xs ml-auto text-red-600"
          >
            <XMarkIcon className="h-4 w-4" />
            Xóa bộ lọc
          </button>
        </div>
      )}

      <div className="rounded-lg shadow">
        <TableComponent
          headers={headers}
          data={feedbacks}
          settings={tableSettings}
        />
      </div>

      <CommentSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        posts={posts || []}
        filters={filters}
        onApply={handleApplyFilters}
      />
    </div>
  );
};

export default Feedback;




